import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  ElementRef,
  viewChild,
  DestroyRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { SearchService } from '../../core/services/search.service';
import { CompanySummary } from '../../core/models/company.model';
import { slugify } from '../../core/utils/slug.utils';

/**
 * Desktop top-bar quick-search ("⌘K"). Debounced suggestions backed by the real
 * SearchService; Enter on a hit opens the company, otherwise runs a full search.
 */
@Component({
  selector: 'app-topbar-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onDocKeydown($event)',
  },
  template: `
    <form class="topbar-search" role="search" (submit)="onSubmit($event)">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
      <input
        #searchInput
        name="q"
        type="search"
        [value]="query()"
        (input)="onInput($event)"
        (focus)="open.set(true)"
        (keydown)="onKeydown($event)"
        placeholder="Caută firmă, CUI sau CAEN…"
        autocomplete="off"
        aria-label="Caută firmă, CUI sau CAEN" />
      <kbd aria-hidden="true">⌘ K</kbd>

      @if (open() && query().length > 1) {
        <div class="topbar-search-pop" role="listbox">
          @if (loading()) {
            <div class="smsg">Se caută…</div>
          } @else if (results().length) {
            <div class="sgroup">Firme</div>
            @for (c of results(); track c.cui; let i = $index) {
              <a
                [href]="companyUrl(c)"
                role="option"
                [attr.aria-selected]="i === activeIdx()"
                [class.kbd-active]="i === activeIdx()"
                (click)="onPick($event, c)">
                <span class="snm">{{ c.name }}</span>
                <span class="meta">CUI {{ c.cui }}</span>
              </a>
            }
          } @else {
            <div class="smsg">Nicio potrivire. Apasă Enter pentru a căuta în toată baza.</div>
          }
        </div>
      }
    </form>
  `,
  styles: [`
    .topbar-search {
      flex: 1; max-width: 340px; min-width: 0; height: 38px;
      background: var(--surface-2); border: 1px solid var(--border); border-radius: 11px;
      display: flex; align-items: center; gap: 10px; padding: 0 12px;
      color: var(--text-subtle); font-size: 13.5px; position: relative;
      transition: border-color .12s, background .12s;
    }
    .topbar-search:focus-within { border-color: var(--accent); background: var(--bg-elev); }
    .topbar-search input {
      flex: 1; min-width: 0; background: transparent; border: 0; outline: none;
      color: var(--text); font: inherit; font-size: 13.5px; padding: 0;
    }
    .topbar-search input::placeholder { color: var(--text-subtle); }
    .topbar-search input::-webkit-search-cancel-button { display: none; }
    .topbar-search kbd {
      font-family: var(--font-mono); font-size: 10.5px; background: var(--bg-elev);
      padding: 2px 6px; border-radius: 5px; border: 1px solid var(--border);
      color: var(--text-muted); flex-shrink: 0;
    }
    .topbar-search:focus-within kbd { display: none; }
    .topbar-search-pop {
      position: absolute; left: -1px; right: -1px; top: calc(100% + 6px);
      background: var(--bg-elev); border: 1px solid var(--border); border-radius: 12px;
      box-shadow: 0 12px 32px rgba(0,0,0,0.35); padding: 6px; z-index: 60;
      max-height: 420px; overflow: auto;
    }
    .sgroup { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); font-weight: 600; padding: 8px 10px 4px; }
    .smsg { padding: 14px 12px; color: var(--text-muted); font-size: 13px; }
    .topbar-search-pop a { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 8px; color: var(--text); text-decoration: none; font-size: 13.5px; }
    .topbar-search-pop a:hover, .topbar-search-pop a.kbd-active { background: var(--surface-2); }
    .topbar-search-pop .snm { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .topbar-search-pop .meta { color: var(--text-muted); font-size: 11.5px; font-family: var(--font-mono); margin-left: auto; flex-shrink: 0; }
  `],
})
export class TopbarSearchComponent {
  private readonly searchService = inject(SearchService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly query$ = new Subject<string>();

  protected readonly query = signal('');
  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly results = signal<CompanySummary[]>([]);
  protected readonly activeIdx = signal(0);

  constructor() {
    this.query$
      .pipe(
        debounceTime(220),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.trim().length < 2) {
            this.loading.set(false);
            return of(null);
          }
          this.loading.set(true);
          return this.searchService
            .search({ search: q.trim(), first: 0, rows: 6 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.loading.set(false);
        this.results.set(res?.companies ?? []);
        this.activeIdx.set(0);
      });
  }

  protected onInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.query.set(v);
    this.open.set(true);
    this.query$.next(v);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const list = this.results();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIdx.set(Math.min(list.length - 1, this.activeIdx() + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIdx.set(Math.max(0, this.activeIdx() - 1));
    } else if (event.key === 'Enter') {
      const hit = list[this.activeIdx()];
      if (hit) {
        event.preventDefault();
        this.go(hit);
      }
    } else if (event.key === 'Escape') {
      this.open.set(false);
      this.searchInput()?.nativeElement.blur();
    }
  }

  protected onDocKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      const el = this.searchInput()?.nativeElement;
      el?.focus();
      el?.select();
    }
  }

  protected onSubmit(event: Event): void {
    event.preventDefault();
    const q = this.query().trim();
    this.open.set(false);
    this.router.navigate(['/cauta-firme'], q ? { queryParams: { search: q } } : {});
  }

  protected onPick(event: Event, c: CompanySummary): void {
    event.preventDefault();
    this.go(c);
  }

  private go(c: CompanySummary): void {
    this.open.set(false);
    if (isPlatformBrowser(this.platformId)) {
      this.searchInput()?.nativeElement.blur();
    }
    this.router.navigateByUrl(this.companyUrl(c));
  }

  protected companyUrl(c: CompanySummary): string {
    return `/companie/${c.cui}/${slugify(c.name ?? '')}`;
  }
}
